import {gql} from '@apollo/client';
import React from 'react';

import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  GetPlateSchemaForNoteLinkedPlateDesign,
  GetPlateSchemaForNoteLinkedPlateDesignVariables,
  GetPlateSchemaForNoteLinkedPlateDesign_storableSchema as GetPlateDesignSchemaStorableSchema,
} from 'notebook/integrations/plate-maps/plate-design/action-buttons/__generated/GetPlateSchemaForNoteLinkedPlateDesign';
import {
  PlateDesign,
  PlateDesignStatus,
} from 'notebook/integrations/plate-maps/plate-design/NoteLinkedPlateDesignTypes';
import notifications from 'notifications';
import {ContainerSchema, PlateSchema, PlateSchemaType} from 'samples/storables/StorableSchemaConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {withLoadingScreen} from 'util/Promises';

export const GET_PLATE_SCHEMA_QUERY = gql`
  query GetPlateSchemaForNoteLinkedPlateDesign($id: StrictString!) @engComponent(name: INVENTORY) @skipAudit {
    storableSchema(id: $id) {
      ...PlateSchemaGraphQL
      ...InaccessibleInfoGraphQL
      ... on PlateSchemaNode {
        id
        containerSchema {
          ...ContainerSchemaGraphQL
        }
      }
    }
  }
  ${getFragmentWrapper('PlateSchema').fragment}
  ${getFragmentWrapper('ContainerSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function getPlateSchemaErrorMessage(
  storableSchema: GetPlateDesignSchemaStorableSchema,
  invalidErrorText: string,
  plateMapSize: number
): string | null {
  if (isInaccessible(storableSchema)) {
    return `${invalidErrorText}: you do not have access to the plate schema.`;
  }
  if (
    storableSchema.__typename !== 'PlateSchemaNode' ||
    storableSchema.plateSchemaType !== PlateSchemaType.FIXED_PLATE
  ) {
    return `${invalidErrorText}: schema is not a well plate schema.`;
  }
  if (isArchived(storableSchema)) {
    return `${invalidErrorText}: schema is archived.`;
  }
  if (storableSchema.width * storableSchema.height !== plateMapSize) {
    return `${invalidErrorText}: schema size has changed.`;
  }
  return null;
}

export interface PlateDesignSummaryActionButtonProps {
  buttonStyle: ButtonStyle;
  /** The text to display on the action button */
  buttonText?: string;
  disabled?: boolean;
  /** The icon to display on the action button */
  icon: BenchlingIconName;
  /** Text to prepend to the error message if the plate design's schema is not valid */
  invalidPlateSchemaErrorText: string;
  plateDesign: PlateDesign;
  /** A function to be called when the button is clicked, if everything is fetched & accessible */
  onDoAction: ({
    /** The schema for the plate well containers */
    containerSchema,
    /** The schema for the plate */
    plateSchema,
  }: {
    containerSchema: ContainerSchema;
    plateSchema: PlateSchema;
  }) => Promise<void>;
  dataTestAttrs?: DataTestAttrs;
  /** Tooltip text. No tooltip if null is given. */
  tooltipText: string | null;
}

export default function PlateDesignSummaryActionButton({
  buttonStyle,
  buttonText,
  disabled,
  icon,
  invalidPlateSchemaErrorText,
  plateDesign,
  onDoAction,
  dataTestAttrs,
  tooltipText,
}: PlateDesignSummaryActionButtonProps): JSX.Element {
  return (
    <Tooltip text={tooltipText} isDisabled={tooltipText == null}>
      <Button
        size={ButtonSize.MINI}
        icon={icon}
        buttonStyle={buttonStyle}
        disabled={disabled}
        onClick={async () => {
          if (disabled) {
            return;
          }

          // We fetch the plate schema on-demand to avoid slowing down initial load via unnecessary queries.
          const plateSchemaApiId =
            plateDesign.status === PlateDesignStatus.INITIAL
              ? plateDesign.data.plateMap.plateSchemaApiId
              : plateDesign.data.plateDesignInput.plateMap.plate_schema_api_id;

          const data = await withLoadingScreen(
            doQuerySafe<
              GetPlateSchemaForNoteLinkedPlateDesign,
              GetPlateSchemaForNoteLinkedPlateDesignVariables
            >({query: GET_PLATE_SCHEMA_QUERY}, {variables: {id: plateSchemaApiId}})
          );

          const errorMessage = getPlateSchemaErrorMessage(
            data.storableSchema,
            invalidPlateSchemaErrorText,
            plateDesign.status === PlateDesignStatus.INITIAL
              ? plateDesign.data.plateMap.positions.length
              : plateDesign.data.plateMap.wells.length
          );
          if (errorMessage != null) {
            notifications.error(errorMessage);
            return;
          }

          // This assertion is already checked in getPlateSchemaErrorMessage but narrows typing here.
          assert(
            data.storableSchema.__typename === 'PlateSchemaNode',
            `Expected schema referenced by NoteLinkedPlateDesign to be PlateSchemaNode, not ${data.storableSchema.__typename}`,
            {plateSchemaApiId}
          );
          assert(
            data.storableSchema.containerSchema != null,
            'Expected container schema to be present in plate schema',
            {
              plateSchemaApiId,
            }
          );

          const containerSchema = graphQLToREST('ContainerSchema', data.storableSchema.containerSchema);
          const plateSchema = graphQLToREST('PlateSchema', data.storableSchema);
          await onDoAction({containerSchema, plateSchema});
        }}
        text={buttonText}
        dataTestAttrs={dataTestAttrs}
      />
    </Tooltip>
  );
}
